import React from 'react';
/* eslint-disable no-unused-vars */

import { appInsight, collectProperties } from 'components/appInsight';
import { Layout } from 'components/Layout';
import PageContent from 'components/page-content/generic-page/PageContent';
import { Seo } from 'components/seo';
import { mergeTagsFromSeoData } from 'components/seo';
import { FourZeroFour } from 'components/simlpleMessage';
import { AppFragment } from 'enums/path-fragment';
import { getAllStaticPage } from 'lib/datoCms/queries/getAllStaticPage';
import { getStaticPage } from 'lib/datoCms/queries/getStaticPage';
import { cleanConfig } from 'lib/datoCms/utils';
import { DatoCMSResponse } from 'lib/types/dato-cms.response';
import { GetStaticProps, InferGetStaticPropsType } from 'next';
import { getDatoCMSData } from 'sites/snai/lib/datoCms/queries/getDatoCMSData';
import { PagesPathArray } from 'types/pages';
import { ISR_REVALIDATE } from 'utility/constant';
import { createPaths, purgeNulls, skipStaticPageGeneration } from 'utility/functions';
import { doGet as getAccessToken } from 'lib/auth/ssr';

/* eslint-enable no-unused-vars */

const InfoPage = ({
  site,
  header,
  footer,
  seoData,
  needHelp,
  cmsContent,
}: InferGetStaticPropsType<typeof getStaticProps>) => {
  const { seoMetaTags, canonical, ...pageData } = cmsContent ?? {};

  const isEmpty = purgeNulls(pageData) === undefined;

  return (
    <Layout header={header} needHelp={needHelp} footer={footer}>
      <Seo siteSeo={site} metaTags={seoMetaTags} canonicalUrl={canonical} />
      {isEmpty ? (
        <FourZeroFour />
      ) : (
        <PageContent title={pageData?.title ?? seoData?.titoloDiRiferimento} staticPage={pageData} seoData={seoData} />
      )}
    </Layout>
  );
};

// eslint-disable-next-line no-unused-vars
export async function getStaticPaths({ locales }) {
  console.log('getStaticPaths : INFO');
  const paths: PagesPathArray[] = [];

  getAccessToken();

  if (!skipStaticPageGeneration()) {
    const { allStaticPages: allPages } = await getAllStaticPage();
    const slugs = createPaths(allPages).filter((x) => x.startsWith(AppFragment.Info));

    locales?.forEach((language) => {
      slugs.forEach((path) => {
        const slug = path.split('/').filter((x) => !!x);
        // console.log(`${path}->${slug}`, language);
        paths.push({ params: { slug }, locale: language });
      });
    });
  }

  return {
    paths: paths,
    fallback: 'blocking',
  };
}

export const getStaticProps: GetStaticProps = async ({ params, locale }) => {
  console.log(`## getStaticProps INFO`);

  const options: Array<string> = Array.isArray(params?.slug) ? params.slug : [params?.slug ?? ''];
  let slug = options
    .filter((x) => !!x)
    .map((x) => `${x}`.toLowerCase())
    .join('/');

  try {
    if (options[0] !== AppFragment.Info) {
      options.unshift(AppFragment.Info);
      slug = options.join('/');
    }
    const promises: Array<Promise<any>> = [];

    const prCmsData = getDatoCMSData('common', locale);
    promises.push(prCmsData);

    const prStaticPage = getStaticPage(slug, locale);
    promises.push(prStaticPage);

    const [cmsData, pageData] = await Promise.all(promises);
    const { site, header, needHelp, footer, allLabels: labels, config } = cmsData as DatoCMSResponse;
    const { tabsWithAccordionModel: seoData } = pageData ?? {};

    const cmsContent = mergeTagsFromSeoData({
      seoData,
      cmsData: pageData.allStaticPages,
      fieldName: '0',
    });

    return Promise.resolve({
      props: purgeNulls({
        site,
        header,
        footer,
        labels,
        config: cleanConfig(config),
        seoData: seoData ?? null,
        needHelp,
        cmsContent: cmsContent?.[0] ?? null,
      }) as any,
      revalidate: ISR_REVALIDATE * 10,
    });
  } catch (exception) {
    appInsight?.trackException({
      exception,
      id: slug,
      properties: collectProperties(true),
    });

    return Promise.resolve({ notFound: true });
  }
};

export default InfoPage;
